import { MobXProviderContext } from "mobx-react";
import { AutoComplete, AutoCompleteCompleteEvent } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { confirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { Toast } from 'primereact/toast';
import { ToggleButton } from "primereact/togglebutton";
import { classNames } from "primereact/utils";
import React, { useContext, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { CARE_PLAN_MEDICATION_MANAGEMENT_TYPES, CarePlanSectionProps, NA, NO, REQUIRE_NURSE_DELEGATION, RESIDENT_CARE_PLAN_TYPE, RESIDENT_CARE_PLAN_TYPE_MESSAGES, YES } from "../../../constants/constants";
import { ASSISTANCE_REQUIRED_MESSAGE, RESIDENT_STRENGTH_MESSAGE } from "../../../constants/carePlanConstants";
import SectionGenieDialog from "./SectionGenieDialog";
import {
  MEDICATION_MANAGEMENT_GENIE_FIELD_DEFINITIONS,
  MEDICATION_MANAGEMENT_GENIE_QUESTIONS,
  MedicationManagementGenieSectionValues,
} from "./medicationManagementGenie";
import { useCarePlanAutoSave } from "./useCarePlanAutoSave";

const EMPTY_MEDICATION_ENTRY = {
  medicationName: "",
  isPsychotropic: false,
  psychotropicDrugClass: "",
  dosage: "",
  benefits: "",
  risks: "",
};

interface PsychotropicMedicationSuggestion {
  productName: string;
  drugClass: string;
}

const CARE_PLAN_FIELD_STYLE: React.CSSProperties = {
  minWidth: "250px",
  flex: "1 1 calc(33% - 1em)",
};

const CARE_PLAN_BOOLEAN_FIELD_STYLE: React.CSSProperties = {
  ...CARE_PLAN_FIELD_STYLE,
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-start",
  gap: "0.75rem",
  padding: "0.25rem 0",
};

const CARE_PLAN_BOOLEAN_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "0.75rem",
  width: "100%",
  flexWrap: "nowrap",
};

const CARE_PLAN_BOOLEAN_LABEL_STYLE: React.CSSProperties = {
  flex: "0 1 auto",
  marginBottom: 0,
};

const CARE_PLAN_READONLY_BOX_STYLE: React.CSSProperties = {
  whiteSpace: "pre-wrap",
  border: "1px dashed lightgray",
  padding: "0.5em",
  borderRadius: "8px",
};

const MEDICATION_LIST_SPLIT_REGEX = /\r?\n|;|•|·/g;
const MEDICATION_NUMBERING_REGEX = /\s+\d+\.\s+/g;
const DOSAGE_HINT_REGEX = /\b(mg|mcg|g|ml|units?|tablet|tablets|tab|tabs|capsule|capsules|cap|caps|poq|po|bid|tid|qid|qhs|hs|prn|daily|weekly)\b/i;

const extractMedicationNames = (value: string): string[] => {
  const cleanedValue = String(value || "")
    .replace(MEDICATION_NUMBERING_REGEX, "\n")
    .replace(MEDICATION_LIST_SPLIT_REGEX, "\n")
    .replace(/\t/g, " ")
    .trim();

  if (!cleanedValue) {
    return [];
  }

  const initialLines = cleanedValue
    .split(/\r?\n/)
    .map((line) => line.replace(/^[\-\u2022\s]+/, "").trim())
    .filter(Boolean);

  const expandedLines = initialLines.flatMap((line) => {
    if (line.includes(",") && !DOSAGE_HINT_REGEX.test(line)) {
      const commaParts = line.split(",").map((part) => part.trim()).filter(Boolean);
      if (commaParts.length > 1) {
        return commaParts;
      }
    }
    return [line];
  });

  return Array.from(new Set(expandedLines));
};

const normalizeMedicationEntries = (entries: any[] | undefined, fallbackRawText: string | undefined) => {
  if (Array.isArray(entries) && entries.length > 0) {
    return entries
      .flatMap((entry) => {
        const normalizedEntry = {
          medicationName: entry?.medicationName || entry?.name || "",
          isPsychotropic: !!entry?.isPsychotropic,
          psychotropicDrugClass: entry?.psychotropicDrugClass || "",
          dosage: entry?.dosage || entry?.dosageRouteFrequency || "",
          benefits: entry?.benefits || entry?.use || "",
          risks: entry?.risks || entry?.specialInstructions || "",
        };
        const medicationNames = extractMedicationNames(normalizedEntry.medicationName);
        const hasOnlyNameContent = !normalizedEntry.dosage && !normalizedEntry.benefits && !normalizedEntry.risks && !normalizedEntry.psychotropicDrugClass;

        if (hasOnlyNameContent && medicationNames.length > 1) {
          return medicationNames.map((medicationName) => ({
            ...normalizedEntry,
            medicationName,
          }));
        }

        return [normalizedEntry];
      })
      .filter((entry) =>
        entry.medicationName
        || entry.dosage
        || entry.benefits
        || entry.risks
        || entry.psychotropicDrugClass
      );
  }

  const rawLines = extractMedicationNames(String(fallbackRawText || ""));

  return rawLines.map((line) => ({
    ...EMPTY_MEDICATION_ENTRY,
    medicationName: line,
  }));
};

const MedicationManagementSection: React.FC<CarePlanSectionProps> = ({
  sectionData, submitButtonClick, backButtonClick, nextButtonClick, showHelpMessage, loadingCarePlan
}) => {
  const { backendAPIStore } = useContext(MobXProviderContext) as any;
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [showGenieDialog, setShowGenieDialog] = useState<boolean>(false);
  const [medicationSuggestions, setMedicationSuggestions] = useState<Record<number, PsychotropicMedicationSuggestion[]>>({});
  const [generatingMedicationAi, setGeneratingMedicationAi] = useState<Record<number, boolean>>({});

  const toast = useRef<Toast>(null);
  const migratedMedicationListText = sectionData?.assessmentMedicationListRawText || sectionData?.medicationAmountReasonableDetails || "";
  const normalizedMedicationEntries = normalizeMedicationEntries(sectionData?.medicationEntries, migratedMedicationListText);

  const defaultValues = {
    carePlanType: RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT,
    anyAllergies: sectionData?.anyAllergies || false,
    allergiesDescribed: sectionData?.allergiesDescribed || '',

    needMoreThanOneMedication: sectionData?.needMoreThanOneMedication || false,
    anyPsychopharmacologicMedication: sectionData?.anyPsychopharmacologicMedication || false,

    seeMARForMedicationDetails: sectionData?.seeMARForMedicationDetails || false,
    medsOrderedBy: sectionData?.medsOrderedBy || '',
    medsDeliveredBy: sectionData?.medsDeliveredBy || '',
    medsPharmacyPacked: sectionData?.medsPharmacyPacked || false,
    medsPharmacyPackedDetails: sectionData?.medsPharmacyPackedDetails || '',

    selfAdministerMedications: sectionData?.selfAdministerMedications || false,
    selfAdministerMedicationsWithAssistance: sectionData?.selfAdministerMedicationsWithAssistance || false,
    medicationAdministeredByStaff: sectionData?.medicationAdministeredByStaff || false,

    medicationManagementTypes: sectionData?.medicationManagementTypes || [],
    nurseDelegationContact: sectionData?.nurseDelegationContact || '',
    assessmentMedicationListRawText: migratedMedicationListText,
    medicationEntries: normalizedMedicationEntries,

    medicationStrengthsDetails: sectionData?.medicationStrengthsDetails || '',
    medicationAssistanceDetails: sectionData?.medicationAssistanceDetails || '',

    medicationAmountReasonableDetails: sectionData?.medicationAmountReasonableDetails || '',

    medicationPlanOutsideAFHStrengthDetails: sectionData?.medicationPlanOutsideAFHStrengthDetails || '',
    medicationPlanOutsideAFHAssistanceDetails: sectionData?.medicationPlanOutsideAFHAssistanceDetails || '',

    medicalRefusalPlanStrengthDetails: sectionData?.medicalRefusalPlanStrengthDetails || '',
    medicalRefusalPlanAssistanceDetails: sectionData?.medicalRefusalPlanAssistanceDetails || '',
  }

  const { control, formState: { errors }, handleSubmit, reset, setValue, watch, getValues } = useForm({ defaultValues });
  const { fields: medicationFields, append: appendMedication, remove: removeMedication } = useFieldArray({
    control,
    name: "medicationEntries",
  });
  const watchAnyAllergies = watch("anyAllergies", defaultValues.anyAllergies);
  const watchMedicationTypes = watch("medicationManagementTypes", defaultValues.medicationManagementTypes);
  const watchMedsPharmacyPacked = watch("medsPharmacyPacked", defaultValues.medsPharmacyPacked);
  const watchMedicationEntries = watch("medicationEntries", defaultValues.medicationEntries);

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const searchMedicationSuggestions = async (index: number, event: AutoCompleteCompleteEvent) => {
    const query = String(event.query || "").trim();
    if (query.length < 1) {
      setMedicationSuggestions((current) => ({ ...current, [index]: [] }));
      return;
    }

    try {
      const response = await backendAPIStore.searchPsychotropicMedicationList(query, 8);
      setMedicationSuggestions((current) => ({
        ...current,
        [index]: Array.isArray(response?.items) ? response.items : [],
      }));
    } catch (error) {
      setMedicationSuggestions((current) => ({ ...current, [index]: [] }));
    }
  };

  const renderMedicationSuggestion = (suggestion: PsychotropicMedicationSuggestion) => (
    <div className="flex flex-column">
      <span className="font-medium">{suggestion.productName}</span>
      <small style={{ color: "#6b7280" }}>{suggestion.drugClass}</small>
    </div>
  );

  const generateMedicationBenefitsAndRisks = async (index: number) => {
    const medicationEntry = getValues("medicationEntries")?.[index] || {};
    const medicationName = String(medicationEntry?.medicationName || "").trim();
    if (!medicationName || generatingMedicationAi[index]) {
      return;
    }

    setGeneratingMedicationAi((current) => ({ ...current, [index]: true }));
    try {
      const response = await backendAPIStore.formatAiText({
        taskType: "professional-note-cleanup",
        tone: "professional",
        domain: "ncp-medication-benefits-risks",
        outputStyle: "plain_text",
        metadata: {
          medicationName,
          currentBenefits: String(medicationEntry?.benefits || "").trim(),
          currentRisks: String(medicationEntry?.risks || "").trim(),
        },
      });
      const generatedText = String(response?.formattedText || "").trim();
      const readGeneratedField = (label: string, nextLabels: string[]) => {
        const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const escapedNextLabels = nextLabels.map((nextLabel) => nextLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
        const nextFieldPattern = escapedNextLabels.length > 0
          ? `(?=\\n(?:${escapedNextLabels.join("|")})\\s*:|$)`
          : "(?=$)";
        const match = generatedText.match(new RegExp(`${escapedLabel}\\s*:\\s*([\\s\\S]*?)${nextFieldPattern}`, "i"));
        return String(match?.[1] || "").trim();
      };
      const generatedBenefits = readGeneratedField("Benefits", ["Risks"]);
      const generatedRisks = readGeneratedField("Risks", []);
      if (generatedBenefits || generatedRisks) {
        if (generatedBenefits) {
          setValue(`medicationEntries.${index}.benefits`, generatedBenefits, { shouldDirty: true, shouldTouch: true });
        }
        if (generatedRisks) {
          setValue(`medicationEntries.${index}.risks`, generatedRisks, { shouldDirty: true, shouldTouch: true });
        }
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "AI generation failed",
        detail: error?.message || "Unable to generate medication benefits and risks.",
        life: 4000,
      });
    } finally {
      setGeneratingMedicationAi((current) => ({ ...current, [index]: false }));
    }
  };

  const onsubmit = async (data: any) => {
    const sanitizedMedicationEntries = (data.medicationEntries || [])
      .map((entry: any) => ({
        medicationName: String(entry?.medicationName || "").trim(),
        isPsychotropic: !!entry?.isPsychotropic,
        psychotropicDrugClass: String(entry?.psychotropicDrugClass || "").trim(),
        dosage: String(entry?.dosage || "").trim(),
        benefits: String(entry?.benefits || "").trim(),
        risks: String(entry?.risks || "").trim(),
      }))
      .filter((entry: any) =>
        entry.medicationName
        || entry.dosage
        || entry.benefits
        || entry.risks
        || entry.psychotropicDrugClass
      );

    const normalizedData = {
      ...data,
      medicationEntries: sanitizedMedicationEntries,
      anyPsychopharmacologicMedication:
        !!data.anyPsychopharmacologicMedication || sanitizedMedicationEntries.some((entry: any) => entry.isPsychotropic),
    };
    submitButtonClick && submitButtonClick(normalizedData);
  }

  const onManualSave = async (data: any) => {
    const sanitizedMedicationEntries = (data.medicationEntries || [])
      .map((entry: any) => ({
        medicationName: String(entry?.medicationName || "").trim(),
        isPsychotropic: !!entry?.isPsychotropic,
        psychotropicDrugClass: String(entry?.psychotropicDrugClass || "").trim(),
        dosage: String(entry?.dosage || "").trim(),
        benefits: String(entry?.benefits || "").trim(),
        risks: String(entry?.risks || "").trim(),
      }))
      .filter((entry: any) =>
        entry.medicationName
        || entry.dosage
        || entry.benefits
        || entry.risks
        || entry.psychotropicDrugClass
      );

    const normalizedData = {
      ...data,
      medicationEntries: sanitizedMedicationEntries,
      anyPsychopharmacologicMedication:
        !!data.anyPsychopharmacologicMedication || sanitizedMedicationEntries.some((entry: any) => entry.isPsychotropic),
    };
    submitButtonClick && submitButtonClick(normalizedData, { showSuccessToast: true });
  }

  const requestRemoveMedication = (index: number) => {
    confirmDialog({
      header: "Remove Medication",
      message: `Remove medication ${index + 1} from this care plan section?`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger p-button-rounded",
      rejectClassName: "p-button-text p-button-rounded",
      accept: () => removeMedication(index),
    });
  };

  useCarePlanAutoSave({
    enabled: enableEditing,
    baselineData: defaultValues,
    watch,
    handleSubmit,
    onSubmit: onsubmit,
  });

  const actions = () => {
    return (
      <div className="flex justify-content-center gap-2">
        <Button label="Prev" icon="pi pi-arrow-left" severity="info" outlined rounded iconPos="left" onClick={backButtonClick} />
        <Button
          label="Use Genie's Help to Fill"
          icon="pi pi-sparkles"
          severity="warning"
          rounded
          onClick={() => {
            setEnableEditing(true);
            setShowGenieDialog(true);
          }}
          visible={enableEditing}
        />
        <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => setEnableEditing(true)} visible={!enableEditing} />
        <Button label="Disable Edit" icon="pi pi-lock" visible={enableEditing} severity="secondary" outlined rounded onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
        <Button label="Save" loading={loadingCarePlan} disabled={!enableEditing} severity="warning" outlined rounded onClick={handleSubmit(onManualSave)} visible={enableEditing} />
        <Button label="Next" icon="pi pi-arrow-right" severity="info" outlined rounded iconPos="right"
          onClick={nextButtonClick} />
      </div>
    )
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>
      <SectionGenieDialog
        visible={showGenieDialog}
        title="Using Genie now"
        questions={MEDICATION_MANAGEMENT_GENIE_QUESTIONS}
        fieldDefinitions={MEDICATION_MANAGEMENT_GENIE_FIELD_DEFINITIONS}
        currentValues={getValues() as MedicationManagementGenieSectionValues}
        successMessage="Medication management care plan updated."
        errorMessage="Unable to update the medication management care plan."
        onHide={() => setShowGenieDialog(false)}
        onApply={async (payload) => {
          const nextValues = {
            ...getValues(),
            ...payload.valueUpdates,
          };

          Object.entries(payload.valueUpdates).forEach(([fieldKey, fieldValue]) => {
            setValue(fieldKey as keyof typeof defaultValues, fieldValue as never, { shouldDirty: true });
          });

          await onsubmit(nextValues);
        }}
      />

      <Card className="resident-careplan-card" footer={actions()} header={actions()} >
        <div className="">
          <form className="">
            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Allergies</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={CARE_PLAN_BOOLEAN_FIELD_STYLE}>
                      <div style={CARE_PLAN_BOOLEAN_ROW_STYLE}>
                        <label style={CARE_PLAN_BOOLEAN_LABEL_STYLE} className={classNames({ 'p-error': errors.anyAllergies })}>Any medication allergies:</label>
                        <Controller
                          name="anyAllergies"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyAllergies')}
                    </div>
                    {
                      watchAnyAllergies && (
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="p-float-label">
                            <Controller name="allergiesDescribed" control={control}
                              rules={{ required: 'Please explain the allergies' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="" style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                            <label htmlFor="allergiesDescribed" className={classNames({ 'p-error': errors.allergiesDescribed })}>Allergies described</label>
                          </span>
                          {getFormErrorMessage('allergiesDescribed')}
                        </div>)
                    }
                    <div className="flex-column flex-wrap mt-2" style={CARE_PLAN_BOOLEAN_FIELD_STYLE}>
                      <div style={CARE_PLAN_BOOLEAN_ROW_STYLE}>
                        <label style={CARE_PLAN_BOOLEAN_LABEL_STYLE} className={classNames({ 'p-error': errors.needMoreThanOneMedication })}>Resident needs more than one medication:</label>
                        <Controller
                          name="needMoreThanOneMedication"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('needMoreThanOneMedication')}
                    </div>
                    <div className="flex-column flex-wrap mt-2" style={CARE_PLAN_BOOLEAN_FIELD_STYLE}>
                      <div style={CARE_PLAN_BOOLEAN_ROW_STYLE}>
                        <label style={CARE_PLAN_BOOLEAN_LABEL_STYLE} className={classNames({ 'p-error': errors.anyPsychopharmacologicMedication })}>Any psychopharmacologic medication prescribed:</label>
                        <Controller
                          name="anyPsychopharmacologicMedication"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyPsychopharmacologicMedication')}
                    </div>
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any allergies: </strong> {sectionData?.anyAllergies ? YES : NO}</div>
                    {
                      sectionData?.anyAllergies && (
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                          <strong>Allergies described: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.allergiesDescribed || NA}</span>
                        </div>)
                    }

                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Resident needs more than one medication: </strong> {sectionData?.needMoreThanOneMedication ? YES : NO}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any psychopharmacologic medication: </strong> {sectionData?.anyPsychopharmacologicMedication ? YES : NO}</div>
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Medication - Packaging and Delivery</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={CARE_PLAN_BOOLEAN_FIELD_STYLE}>
                      <div style={CARE_PLAN_BOOLEAN_ROW_STYLE}>
                        <label style={CARE_PLAN_BOOLEAN_LABEL_STYLE} className={classNames({ 'p-error': errors.seeMARForMedicationDetails })}>See MAR for medication details:</label>
                        <Controller
                          name="seeMARForMedicationDetails"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('seeMARForMedicationDetails')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="p-float-label">
                        <Controller name="medsOrderedBy" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="" style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                        <label htmlFor="medsOrderedBy" className={classNames({ 'p-error': errors.medsOrderedBy })}>Medications ordered by</label>
                      </span>
                      {getFormErrorMessage('medsOrderedBy')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="p-float-label">
                        <Controller name="medsDeliveredBy" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="" style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                        <label htmlFor="medsDeliveredBy" className={classNames({ 'p-error': errors.medsDeliveredBy })}>Medications delivered by</label>
                      </span>
                      {getFormErrorMessage('medsDeliveredBy')}
                    </div>
                    <div className="flex-column flex-wrap mt-2" style={CARE_PLAN_BOOLEAN_FIELD_STYLE}>
                      <div style={CARE_PLAN_BOOLEAN_ROW_STYLE}>
                        <label style={CARE_PLAN_BOOLEAN_LABEL_STYLE} className={classNames({ 'p-error': errors.medsPharmacyPacked })}>Are medications pharmacy packed:</label>
                        <Controller
                          name="medsPharmacyPacked"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('medsPharmacyPacked')}
                    </div>
                    {
                      watchMedsPharmacyPacked && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="p-float-label">
                              <Controller name="medsPharmacyPackedDetails" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Enter details for medications pharmacy packed ..." style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <label htmlFor="medsPharmacyPackedDetails" className={classNames({ 'p-error': errors.medsPharmacyPackedDetails })}>Medications pharmacy packed details</label>
                            </span>
                            {getFormErrorMessage('medsPharmacyPackedDetails')}
                          </div>
                        </>)
                    }
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>See MAR for medication details: </strong> {sectionData?.seeMARForMedicationDetails ? YES : NO}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>Medications ordered by: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.medsOrderedBy || NA}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>Medications delivered by: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.medsDeliveredBy || NA}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Are medications pharmacy packed: </strong> {sectionData?.medsPharmacyPacked ? YES : NO}</div>
                    {
                      sectionData?.medsPharmacyPacked && (
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                          <strong>Medications pharmacy packed details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.medsPharmacyPackedDetails || NA}</span>
                        </div>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Medication types</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={CARE_PLAN_BOOLEAN_FIELD_STYLE}>
                      <div style={CARE_PLAN_BOOLEAN_ROW_STYLE}>
                        <label style={CARE_PLAN_BOOLEAN_LABEL_STYLE} className={classNames({ 'p-error': errors.selfAdministerMedications })}>Any self-adminstrater medications:</label>
                        <Controller
                          name="selfAdministerMedications"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('selfAdministerMedications')}
                    </div>
                    <div className="flex-column flex-wrap mt-2" style={CARE_PLAN_BOOLEAN_FIELD_STYLE}>
                      <div style={CARE_PLAN_BOOLEAN_ROW_STYLE}>
                        <label style={CARE_PLAN_BOOLEAN_LABEL_STYLE} className={classNames({ 'p-error': errors.selfAdministerMedicationsWithAssistance })}>Any self-administer medications with assistance:</label>
                        <Controller
                          name="selfAdministerMedicationsWithAssistance"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('selfAdministerMedicationsWithAssistance')}
                    </div>
                    <div className="flex-column flex-wrap mt-2" style={CARE_PLAN_BOOLEAN_FIELD_STYLE}>
                      <div style={CARE_PLAN_BOOLEAN_ROW_STYLE}>
                        <label style={CARE_PLAN_BOOLEAN_LABEL_STYLE} className={classNames({ 'p-error': errors.medicationAdministeredByStaff })}>Any medication administered by caregiver:</label>
                        <Controller
                          name="medicationAdministeredByStaff"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('medicationAdministeredByStaff')}
                    </div>
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.medicationManagementTypes })}>Medication management types:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="medicationManagementTypes"
                          control={control}
                          render={({ field }) => (
                            <MultiSelect placeholder="Select all that apply ..." options={CARE_PLAN_MEDICATION_MANAGEMENT_TYPES} optionLabel="name" optionGroupChildren="items" optionGroupLabel="name"
                              {...field}
                              value={field.value}
                              maxSelectedLabels={3}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('medicationManagementTypes')}
                    </div>
                    {
                      watchMedicationTypes && watchMedicationTypes.find((type) => type === REQUIRE_NURSE_DELEGATION) && (
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="p-float-label">
                            <Controller name="nurseDelegationContact" control={control}
                              rules={{ required: 'Please provide nurse delegation contact details' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Enter RN contact details ..." style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                            <label htmlFor="nurseDelegationContact" className={classNames({ 'p-error': errors.nurseDelegationContact })}>Nurse delegation contact details</label>
                          </span>
                          {getFormErrorMessage('nurseDelegationContact')}
                        </div>
                      )
                    }

                    {/* <div className="col-12 flex-column flex-wrap mt-2">
                      <span className="p-float-label">
                        <Controller name="assessmentMedicationListRawText" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Imported medication list from assessment ..." style={{ width: '100%' }} rows={4} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                        <label htmlFor="assessmentMedicationListRawText" className={classNames({ 'p-error': errors.assessmentMedicationListRawText })}>Imported medication list from assessment</label>
                      </span>
                      {getFormErrorMessage('assessmentMedicationListRawText')}
                    </div> */}

                    <div className="col-12 mt-3">
                      <div style={{ marginBottom: "0.75rem" }}>
                        <Message
                          severity="info"
                          text="Note: Medication list is not included in the generated NCP because the DSHS template does not require them. Any psychotropic medications will be included in the Behavior and Interventions Care Plan document, so make sure to generate that document after updating the details here."
                        />
                      </div>
                      <div className="flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                        <strong>Current medications</strong>
                      </div>
                      <div className="flex flex-column gap-3">
                        {medicationFields.length === 0 && (
                          <div style={{ border: "1px dashed lightgray", padding: "0.75rem", borderRadius: "8px", color: "#64748b" }}>
                            No medications added yet.
                          </div>
                        )}
                        {medicationFields.map((field, index) => (
                          <div
                            key={field.id}
                            style={{
                              border: "1px solid #fde68a",
                              borderRadius: "10px",
                              padding: "0.75rem 0.85rem",
                              background: "#fffaf0",
                            }}
                          >
                            <div className="flex justify-content-between align-items-center mb-2">
                              <strong>Medication {index + 1}</strong>
                              <Button
                                type="button"
                                icon="pi pi-trash"
                                severity="danger"
                                text
                                rounded
                                onClick={() => requestRemoveMedication(index)}
                                style={{ color: "#dc2626" }}
                              />
                            </div>

                            <div className="grid">
                              <div className="col-12 md:col-2">
                                <label className="font-medium text-sm block mb-2">Psychotropic?</label>
                                <Controller
                                  name={`medicationEntries.${index}.isPsychotropic`}
                                  control={control}
                                  render={({ field }) => (
                                    <ToggleButton
                                      onLabel="Yes"
                                      offLabel="No"
                                      checked={!!field.value}
                                      onChange={(e) => {
                                        field.onChange(e.value);
                                        if (!e.value) {
                                          setValue(`medicationEntries.${index}.psychotropicDrugClass`, "", {
                                            shouldDirty: true,
                                            shouldTouch: true,
                                          });
                                        }
                                      }}
                                      className="w-half"
                                    />
                                  )}
                                />
                              </div>
                              <div className="col-12 md:col-4">
                                <label className="font-medium text-sm block mb-2">Medication name</label>
                                <Controller
                                  name={`medicationEntries.${index}.medicationName`}
                                  control={control}
                                  render={({ field }) => (
                                    watchMedicationEntries?.[index]?.isPsychotropic ? (
                                      <AutoComplete
                                        value={field.value}
                                        suggestions={medicationSuggestions[index] || []}
                                        completeMethod={(event) => { void searchMedicationSuggestions(index, event); }}
                                        field="productName"
                                        className="w-full"
                                        inputClassName="w-full p-inputtext-sm"
                                        forceSelection={false}
                                        dropdown={false}
                                        delay={250}
                                        placeholder="Medication name"
                                        itemTemplate={renderMedicationSuggestion}
                                        onChange={(event) => {
                                          const nextValue = typeof event.value === "string"
                                            ? event.value
                                            : event.value?.productName || "";
                                          field.onChange(nextValue);
                                        }}
                                        onSelect={(event) => {
                                          const selectedItem = event.value as PsychotropicMedicationSuggestion;
                                          const className = selectedItem?.drugClass || "";
                                          if (className) {
                                            const currentClass = watchMedicationEntries?.[index]?.psychotropicDrugClass || "";
                                            if (!currentClass) {
                                              setValue(`medicationEntries.${index}.psychotropicDrugClass`, className, {
                                                shouldDirty: true,
                                                shouldTouch: true,
                                              });
                                            }
                                          }
                                        }}
                                      />
                                    ) : (
                                      <InputText {...field} className="w-full p-inputtext-sm" placeholder="Medication name" />
                                    )
                                  )}
                                />
                              </div>
                              {watchMedicationEntries?.[index]?.isPsychotropic
                                && String(watchMedicationEntries?.[index]?.medicationName || "").trim() ? (
                                <div className="col-12 md:col-3">
                                  <label className="font-medium text-sm block mb-2">Drug class</label>
                                  <Controller
                                    name={`medicationEntries.${index}.psychotropicDrugClass`}
                                    control={control}
                                    render={({ field }) => (
                                      <InputText
                                        {...field}
                                        className="w-full p-inputtext-sm"
                                        placeholder="Drug class"
                                      />
                                    )}
                                  />
                                </div>
                              ) : null}
                              <div className={watchMedicationEntries?.[index]?.isPsychotropic
                                && String(watchMedicationEntries?.[index]?.medicationName || "").trim()
                                ? "col-12 md:col-3"
                                : "col-12 md:col-6"}>
                                <label className="font-medium text-sm block mb-2">Dosage</label>
                                <Controller
                                  name={`medicationEntries.${index}.dosage`}
                                  control={control}
                                  render={({ field }) => (
                                    <InputText {...field} className="w-full p-inputtext-sm" placeholder="Dose, route, frequency" />
                                  )}
                                />
                              </div>
                              <div className="col-12 md:col-6">
                                <label className="font-medium text-sm block mb-2">Benefits</label>
                                <Controller
                                  name={`medicationEntries.${index}.benefits`}
                                  control={control}
                                  render={({ field }) => (
                                    <InputTextarea {...field} className="w-full" rows={2} autoResize style={{ minHeight: "44px" }} placeholder="Benefits / intended use" />
                                  )}
                                />
                              </div>
                              <div className="col-12 md:col-6">
                                <label className="font-medium text-sm block mb-2">Risks</label>
                                <Controller
                                  name={`medicationEntries.${index}.risks`}
                                  control={control}
                                  render={({ field }) => (
                                    <InputTextarea {...field} className="w-full" rows={2} autoResize style={{ minHeight: "44px" }} placeholder="Risks / side effects / concerns" />
                                  )}
                                />
                              </div>
                              <div className="col-12 flex justify-content-center">
                                <Button
                                  type="button"
                                  label="Generate with AI"
                                  icon="pi pi-sparkles"
                                  severity="warning"
                                  outlined
                                  rounded
                                  loading={!!generatingMedicationAi[index]}
                                  disabled={!String(watchMedicationEntries?.[index]?.medicationName || "").trim()}
                                  onClick={() => { void generateMedicationBenefitsAndRisks(index); }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="flex justify-content-end">
                          <Button
                            type="button"
                            label="Add Medication"
                            icon="pi pi-plus"
                            severity="warning"
                            rounded
                            onClick={() => appendMedication({ ...EMPTY_MEDICATION_ENTRY })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(100% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="medicationAmountReasonableDetails" className={classNames({ 'p-error': errors.medicationAmountReasonableDetails })}>
                          Describe the reason the resident needs this amount of medication assistance:
                        </label>
                      </span>
                      <span className="p-float-label">
                        <Controller name="medicationAmountReasonableDetails" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Describe the reason the resident needs this amount of medication assistance ..." style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('medicationAmountReasonableDetails')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="medicationStrengthsDetails" className={classNames({ 'p-error': errors.medicationStrengthsDetails })}>
                          {RESIDENT_STRENGTH_MESSAGE}
                        </label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.MEDICATION_TYPE_OVERVIEW_RESIDENT_STRENGTH) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <span className="p-float-label">
                        <Controller name="medicationStrengthsDetails" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Enter medication strengths details ..." style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('medicationStrengthsDetails')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="medicationAssistanceDetails" className={classNames({ 'p-error': errors.medicationAssistanceDetails })}>
                          {ASSISTANCE_REQUIRED_MESSAGE}
                        </label>
                      </span>
                      <span className="p-float-label">
                        <Controller name="medicationAssistanceDetails" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Enter details for medication assistance ..." style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('medicationAssistanceDetails')}
                    </div>
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any self-adminstrater medications: </strong> {sectionData?.selfAdministerMedications ? YES : NO}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any self-administer medications with assistance: </strong> {sectionData?.selfAdministerMedicationsWithAssistance ? YES : NO}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any medication administered by caregiver: </strong> {sectionData?.medicationAdministeredByStaff ? YES : NO}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>Medication management types: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.medicationManagementTypes || []).length > 0 ? (sectionData?.medicationManagementTypes || []).join(', ') : NA}</span>
                    </div>
                    {
                      sectionData?.medicationManagementTypes && sectionData?.medicationManagementTypes.find((type) => type === REQUIRE_NURSE_DELEGATION) && (
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                          <strong>Nurse delegation contact details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.nurseDelegationContact || NA}</span>
                        </div>
                      )
                    }
                    {/* <div className="col-12 flex flex-column" style={{ whiteSpace: "pre-wrap" }}>
                      <strong>Imported medication list from assessment: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>
                        {migratedMedicationListText || NA}
                      </span>
                    </div> */}
                    <div className="col-12 flex flex-column" style={{ whiteSpace: "pre-wrap" }}>
                      <div style={{ marginBottom: "0.75rem" }}>
                        <Message
                          severity="info"
                          text="Note: Medication list is not included in the generated NCP because the DSHS template does not require them. Any psychotropic medications will be included in the Behavior and Interventions Care Plan document, so make sure to generate that document after updating the details here."
                        />
                      </div>
                      <strong>Current medications: </strong>
                      {(watchMedicationEntries || []).length > 0 ? (
                        <div style={{
                          border: "1px solid #d8e5f1",
                          borderRadius: "12px",
                          overflow: "hidden",
                          background: "#ffffff",
                          boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
                        }}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "minmax(160px, 1.2fr) minmax(90px, 0.7fr) minmax(150px, 1fr) minmax(160px, 1fr) minmax(200px, 1.2fr) minmax(200px, 1.2fr)",
                              gap: "0",
                              background: "#eef6fc",
                              borderBottom: "1px solid #d8e5f1",
                              fontWeight: 700,
                              color: "#185da2",
                              fontSize: "0.82rem",
                            }}
                          >
                            <div style={{ padding: "0.7rem 0.8rem" }}>Medication</div>
                            <div style={{ padding: "0.7rem 0.8rem" }}>Psychotropic</div>
                            <div style={{ padding: "0.7rem 0.8rem" }}>Drug Class</div>
                            <div style={{ padding: "0.7rem 0.8rem" }}>Dosage</div>
                            <div style={{ padding: "0.7rem 0.8rem" }}>Benefits</div>
                            <div style={{ padding: "0.7rem 0.8rem" }}>Risks</div>
                          </div>
                          {(watchMedicationEntries || []).map((entry: any, index: number) => (
                            <div
                              key={`${entry?.medicationName || "medication"}-${index}`}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(160px, 1.2fr) minmax(90px, 0.7fr) minmax(150px, 1fr) minmax(160px, 1fr) minmax(200px, 1.2fr) minmax(200px, 1.2fr)",
                                borderBottom: index === (watchMedicationEntries || []).length - 1 ? "none" : "1px solid #e2e8f0",
                                background: index % 2 === 0 ? "#ffffff" : "#f8fbff",
                                fontSize: "0.85rem",
                                color: "#334155",
                              }}
                            >
                              <div style={{ padding: "0.75rem 0.8rem", fontWeight: 600 }}>{entry?.medicationName || NA}</div>
                              <div style={{ padding: "0.75rem 0.8rem" }}>{entry?.isPsychotropic ? YES : NO}</div>
                              <div style={{ padding: "0.75rem 0.8rem" }}>{entry?.isPsychotropic ? (entry?.psychotropicDrugClass || NA) : "—"}</div>
                              <div style={{ padding: "0.75rem 0.8rem", whiteSpace: "pre-wrap" }}>{entry?.dosage || NA}</div>
                              <div style={{ padding: "0.75rem 0.8rem", whiteSpace: "pre-wrap" }}>{entry?.benefits || NA}</div>
                              <div style={{ padding: "0.75rem 0.8rem", whiteSpace: "pre-wrap" }}>{entry?.risks || NA}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{NA}</span>
                      )}
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>Describe the reason the resident needs this amount of medication assistance: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.medicationAmountReasonableDetails || NA}</span>
                    </div>

                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>{RESIDENT_STRENGTH_MESSAGE}</strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.medicationStrengthsDetails || NA}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>{ASSISTANCE_REQUIRED_MESSAGE}</strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.medicationAssistanceDetails || NA}</span>
                    </div>
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Resident medication outside facility</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="medicationPlanOutsideAFHStrengthDetails" className={classNames({ 'p-error': errors.medicationPlanOutsideAFHStrengthDetails })}>
                          Any information related to medication outside facility:
                        </label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.MEDICATION_TYPE_OUTSIDE_AFH_RESIDENT_STRENGTH) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <span className="p-float-label">
                        <Controller name="medicationPlanOutsideAFHStrengthDetails" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Enter medication strengths details ..." style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('medicationPlanOutsideAFHStrengthDetails')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="medicationPlanOutsideAFHAssistanceDetails" className={classNames({ 'p-error': errors.medicationPlanOutsideAFHAssistanceDetails })}>
                          {ASSISTANCE_REQUIRED_MESSAGE}
                        </label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.MEDICATION_TYPE_OUTSIDE_AFH_RESIDENT_ASSISTANCE) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <span className="p-float-label">
                        <Controller name="medicationPlanOutsideAFHAssistanceDetails" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Enter medication assistance details ..." style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('medicationPlanOutsideAFHAssistanceDetails')}
                    </div>
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>Any information related to medication outside facility: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.medicationPlanOutsideAFHStrengthDetails || NA}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>{ASSISTANCE_REQUIRED_MESSAGE}</strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.medicationPlanOutsideAFHAssistanceDetails || NA}</span>
                    </div>
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Resident medication refusal</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="medicalRefusalPlanStrengthDetails" className={classNames({ 'p-error': errors.medicalRefusalPlanStrengthDetails })}>
                          Any information related to medication refusal:
                        </label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.MEDICATION_TYPE_REFUSAL) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <span className="p-float-label">
                        <Controller name="medicalRefusalPlanStrengthDetails" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Enter medication strengths details ..." style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('medicalRefusalPlanStrengthDetails')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="medicalRefusalPlanAssistanceDetails" className={classNames({ 'p-error': errors.medicalRefusalPlanAssistanceDetails })}>
                          {ASSISTANCE_REQUIRED_MESSAGE}
                        </label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.MEDICATION_TYPE_REFUSAL_ASSISTANCE) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <span className="p-float-label">
                        <Controller name="medicalRefusalPlanAssistanceDetails" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Enter medication assistance details ..." style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('medicalRefusalPlanAssistanceDetails')}
                    </div>
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>Any information related to medication refusal: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.medicalRefusalPlanStrengthDetails || NA}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>{ASSISTANCE_REQUIRED_MESSAGE}</strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.medicalRefusalPlanAssistanceDetails || NA}</span>
                    </div>
                  </div>
                )
              }
            </div>
          </form>
        </div>
      </Card >
    </div >
  );
};

export default MedicationManagementSection;


